import { useState } from 'react'
import {
  Alert, Avatar, Button, Drawer, Form, Input, Select, Tag, Space, App,
  Badge, Spin,
} from 'antd'
import {
  PlusOutlined, LogoutOutlined, CameraOutlined, UserOutlined,
  IdcardOutlined, ScanOutlined, WhatsAppOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { businessCardsApi, visitorsApi } from '@/features/gate/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { VisitorLog } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import CameraCapture from '@/shared/components/CameraCapture'
import HostPicker from '@/shared/components/HostPicker'
import { usePermission } from '@/shared/hooks/usePermission'

const PURPOSES = ['VENDOR_MEETING', 'DELIVERY', 'SERVICE_ENGINEER', 'AUDIT',
  'INTERVIEW', 'OFFICIAL_VISIT', 'PERSONAL', 'OTHER']

const statusColor: Record<string, 'processing' | 'success' | 'error' | 'default' | 'warning'> = {
  PENDING_APPROVAL: 'warning',
  INSIDE: 'processing', EXITED: 'success', DENIED: 'error',
}

export default function VisitorLogPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Sprint VIS-PHOTO-1 — gate selfie
  const [cameraOpen, setCameraOpen] = useState(false)
  // Holds the captured File before submit. After create, we POST it to
  // /visitors/{id}/photo. Cleared on drawer close + on submit.
  const [capturedPhoto, setCapturedPhoto] = useState<File | null>(null)
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null)
  // For retake on an existing visitor (from list row): track which row.
  const [retakeTarget, setRetakeTarget] = useState<VisitorLog | null>(null)

  // Sprint 0149 — business card capture + OCR auto-fill
  const [cardCameraOpen, setCardCameraOpen] = useState(false)
  const [cardId, setCardId] = useState<string | null>(null)
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null)
  const [ocrWarning, setOcrWarning] = useState<string | null>(null)
  // Card extract uses the back camera (better for documents) — flipped
  // from the front-facing selfie.
  const extractCardMut = useMutation({
    mutationFn: (file: File) => businessCardsApi.extract(file),
    onSuccess: (data) => {
      setCardId(data.card_id)
      setCardImageUrl(data.image_url)
      // Auto-fill any field the OCR pulled, but never overwrite what
      // the operator already typed — partial OCR is better than wiping
      // a manual entry.
      const current = form.getFieldsValue()
      const patch: Record<string, unknown> = {}
      if (data.extracted_name && !current.visitor_name)
        patch.visitor_name = data.extracted_name
      if (data.extracted_company && !current.visitor_company)
        patch.visitor_company = data.extracted_company
      if (data.extracted_mobile && !current.visitor_mobile)
        patch.visitor_mobile = data.extracted_mobile
      if (data.extracted_designation && !current.remarks)
        patch.remarks = `${data.extracted_designation}${data.extracted_email ? ' · ' + data.extracted_email : ''}`
      form.setFieldsValue(patch)
      setOcrWarning(data.ocr_available ? null : (data.ocr_error || 'OCR unavailable on server. Type details manually.'))
      const ocrFieldCount = [
        data.extracted_name, data.extracted_company, data.extracted_mobile,
        data.extracted_email, data.extracted_designation,
      ].filter(Boolean).length
      if (data.ocr_available && ocrFieldCount === 0) {
        message.warning('Card saved but no fields could be extracted. Type manually.')
      } else if (data.ocr_available) {
        message.success(`Card scanned — ${ocrFieldCount} field(s) auto-filled. Review and edit.`)
      } else {
        message.info('Card image saved. OCR unavailable; type details manually.')
      }
      setCardCameraOpen(false)
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      message.error(e?.response?.data?.detail ?? 'Card capture failed')
    },
  })

  const canCreate = usePermission('visitor.create')
  const canUpdate = usePermission('visitor.update')

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 200 }),
  })
  const plantOptions = plantsData?.items?.map((p: { id: string; name: string }) => ({ value: p.id, label: p.name })) ?? []
  const firstPlantId = plantOptions[0]?.value

  const { data, isLoading } = useQuery({
    queryKey: ['visitors', page, pageSize, search, statusFilter],
    queryFn: () => visitorsApi.list({ page, size: pageSize, search: search || undefined, status: statusFilter }),
  })

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      // 1. Create the visitor row first to get the ID.
      const visitor = await visitorsApi.create(values)
      // 2. If supervisor captured a photo, upload it now. Wrapped so
      //    a photo failure doesn't lose the visitor row — they can
      //    retake from the list row later.
      if (capturedPhoto && visitor.id) {
        try {
          await visitorsApi.uploadPhoto(visitor.id, capturedPhoto)
        } catch (e) {
          const err = e as { response?: { data?: { detail?: string } } }
          message.warning(
            `Visitor created, but photo upload failed: ${err?.response?.data?.detail ?? 'unknown'}. Use Retake on the list row.`,
          )
        }
      }
      // 3. Link the captured business card to this visitor so the
      //    library shows visitor_log_id and the card row is no longer
      //    "orphaned" in the library list.
      if (cardId && visitor.id) {
        try {
          await businessCardsApi.linkToVisitor(visitor.id, cardId)
        } catch {
          // non-fatal — card stays in library unlinked
        }
      }
      return visitor
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitors'] })
      message.success(capturedPhoto ? 'Visitor logged in with photo' : 'Visitor logged in')
      setDrawerOpen(false)
      form.resetFields()
      if (capturedPreview) URL.revokeObjectURL(capturedPreview)
      setCapturedPhoto(null)
      setCapturedPreview(null)
      setCardId(null)
      setCardImageUrl(null)
      setOcrWarning(null)
    },
    onError: (e: Error) => message.error(
      (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed',
    ),
  })

  // Sprint VIS-PHOTO-1 — retake / capture for an existing visitor
  // (row action). Used when supervisor missed the photo at entry.
  const retakeMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      visitorsApi.uploadPhoto(id, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitors'] })
      message.success('Photo updated')
      setRetakeTarget(null)
    },
    onError: () => message.error('Photo upload failed'),
  })

  const exitMutation = useMutation({
    mutationFn: (id: string) => visitorsApi.recordExit(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitors'] })
      message.success('Visitor exit recorded')
    },
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    {
      title: 'Photo', key: 'photo', width: 60,
      render: (_: unknown, r: VisitorLog) => (
        <Avatar
          src={r.photo_url || undefined}
          icon={<UserOutlined />}
          size={36}
          style={{ background: r.photo_url ? undefined : '#bfbfbf' }}
        />
      ),
    },
    { title: 'Pass No', dataIndex: 'visitor_pass_no', key: 'pass', width: 120 },
    { title: 'Name', dataIndex: 'visitor_name', key: 'name' },
    { title: 'Company', dataIndex: 'visitor_company', key: 'company', render: (v?: string) => v ?? '—' },
    { title: 'Mobile', dataIndex: 'visitor_mobile', key: 'mobile', render: (v?: string) => v ?? '—' },
    {
      title: 'Purpose', dataIndex: 'purpose', key: 'purpose',
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
    },
    { title: 'Person to Meet', dataIndex: 'person_to_meet', key: 'meet', render: (v?: string) => v ?? '—' },
    {
      title: 'Entry Time', dataIndex: 'entry_time', key: 'in',
      render: (v: string) => new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
    },
    {
      title: 'Exit Time', dataIndex: 'actual_exit_time', key: 'out',
      render: (v?: string) => v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: string) => <Badge status={statusColor[v] ?? 'default'} text={v} />,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: VisitorLog) => {
        // Only show "Nudge host" while the host actually has the chance
        // to act — once approved/rejected/exited it's pointless. Also
        // requires we have the host's phone on file. If host_phone is
        // missing, the button is hidden (not disabled) so the gate
        // operator isn't confused by a non-functional control.
        const canNudgeHost =
          record.status === 'PENDING_APPROVAL' &&
          record.approval_status === 'PENDING' &&
          !!record.host_phone

        const onNudgeHost = () => {
          // Normalise the phone: keep digits only, prepend country code
          // if missing. Indian phones in our DB are stored as 10-digit
          // numbers most often; wa.me requires the full international
          // form with no plus.
          const digits = (record.host_phone ?? '').replace(/\D/g, '')
          const intl = digits.length === 10 ? `91${digits}` : digits
          const text = encodeURIComponent(
            `Visitor ${record.visitor_name}` +
            (record.visitor_company ? ` from ${record.visitor_company}` : '') +
            ` is at the gate (pass ${record.visitor_pass_no}). ` +
            `Please open the ERP and approve/reject.`,
          )
          // Open in a new tab/window — on phone it deep-links into the
          // WhatsApp app, on desktop it opens WhatsApp Web.
          window.open(`https://wa.me/${intl}?text=${text}`, '_blank',
            'noopener,noreferrer')
        }

        return (
          <Space size="small">
            {canNudgeHost && (
              <Button
                size="small"
                icon={<WhatsAppOutlined />}
                style={{ color: '#25D366', borderColor: '#25D366' }}
                onClick={onNudgeHost}
                title={`Nudge ${record.host_name ?? 'host'} on WhatsApp`}
              >
                Nudge host
              </Button>
            )}
            {canUpdate && (
              <Button size="small" icon={<CameraOutlined />}
                onClick={() => setRetakeTarget(record)}>
                {record.photo_url ? 'Retake' : 'Photo'}
              </Button>
            )}
            {canUpdate && record.status === 'INSIDE' && (
              <Button size="small" icon={<LogoutOutlined />} danger
                onClick={() => exitMutation.mutate(record.id)}>Exit</Button>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader title="Visitor Log" subtitle="Digital visitor register at security gate"
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields()
            form.setFieldsValue({ purpose: 'OTHER', plant_id: firstPlantId })
            setDrawerOpen(true)
          }}>
            Visitor IN
          </Button>
        )}
      />
      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="Name, company, pass no…" allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }} style={{ width: 260 }} />
        <Select placeholder="Status" allowClear style={{ width: 140 }}
          options={['INSIDE', 'EXITED', 'DENIED'].map(s => ({ value: s, label: s }))}
          onChange={(v) => { setStatusFilter(v); setPage(1) }} />
      </Space>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading}
        total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      <FormDrawer title="Visitor Entry" open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          if (capturedPreview) URL.revokeObjectURL(capturedPreview)
          setCapturedPhoto(null); setCapturedPreview(null)
        }} onSubmit={onSubmit}
        loading={saveMutation.isPending} form={form}>
        {/* Sprint 0149 — Business card capture + OCR auto-fill.
            Operator scans the visitor's business card; backend runs
            Tesseract OCR; name/company/mobile fields auto-fill. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: 12, background: '#fffbe6', borderRadius: 8,
          marginBottom: 12, border: '1px solid #ffe58f',
        }}>
          {cardImageUrl ? (
            <img
              src={cardImageUrl}
              alt="Business card"
              style={{
                width: 72, height: 48, objectFit: 'cover', borderRadius: 4,
              }}
            />
          ) : (
            <Avatar
              icon={<IdcardOutlined />}
              size={48}
              shape="square"
              style={{ background: '#faad14' }}
            />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>Business card</div>
            <div style={{ fontSize: 12, color: '#888' }}>
              {extractCardMut.isPending
                ? <Spin size="small" />
                : cardImageUrl
                  ? 'Captured & scanned. Review the auto-filled fields below.'
                  : 'Scan the visitor’s business card to auto-fill details.'}
            </div>
          </div>
          <Button
            type={cardImageUrl ? 'default' : 'primary'}
            icon={<ScanOutlined />}
            onClick={() => setCardCameraOpen(true)}
            loading={extractCardMut.isPending}
          >
            {cardImageUrl ? 'Re-scan' : 'Scan card'}
          </Button>
        </div>
        {ocrWarning && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={ocrWarning}
          />
        )}

        {/* Sprint VIS-PHOTO-1 — Capture photo at top of the form so the
            supervisor remembers to take it BEFORE typing details. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: 12, background: '#fafafa', borderRadius: 8,
          marginBottom: 16,
        }}>
          <Avatar
            src={capturedPreview || undefined}
            icon={<UserOutlined />}
            size={64}
            style={{ background: capturedPreview ? undefined : '#bfbfbf' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>Visitor photo</div>
            <div style={{ fontSize: 12, color: '#888' }}>
              {capturedPhoto
                ? 'Captured — will upload with the entry.'
                : 'Take a quick selfie of the visitor at the gate.'}
            </div>
          </div>
          <Button
            type={capturedPhoto ? 'default' : 'primary'}
            icon={<CameraOutlined />}
            onClick={() => setCameraOpen(true)}
          >
            {capturedPhoto ? 'Retake' : 'Capture'}
          </Button>
        </div>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="visitor_name" label="Visitor Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="visitor_company" label="Company / Organisation">
          <Input />
        </Form.Item>
        <Form.Item name="visitor_mobile" label="Mobile No">
          <Input />
        </Form.Item>
        <Form.Item name="purpose" label="Purpose of Visit" rules={[{ required: true }]}>
          <Select options={PURPOSES.map(p => ({ value: p, label: p.replace(/_/g, ' ') }))} />
        </Form.Item>
        {/* Sprint 0150 — Replaces free-text with authorised hosts dropdown.
            host_employee_id is required; backend rejects entries without it. */}
        <Form.Item
          name="host_employee_id"
          label="Person to Meet"
          rules={[{ required: true, message: 'Pick an authorised host' }]}
          extra="The host gets a phone-ring notification and must Approve before the visitor is sent in."
        >
          <HostPicker plantId={firstPlantId} />
        </Form.Item>
        <Form.Item name="department_to_visit" label="Department">
          <Input />
        </Form.Item>
        <Form.Item name="vehicle_number" label="Vehicle Number">
          <Input />
        </Form.Item>
        <Form.Item name="items_carried" label="Items Carried">
          <Input placeholder="Laptop, bag, tools…" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>

      {/* Sprint VIS-PHOTO-1 — Camera at entry time (held until Save) */}
      <Drawer
        title="Capture Visitor Photo"
        width={520}
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        destroyOnClose
      >
        <CameraCapture
          onCapture={(file) => {
            if (capturedPreview) URL.revokeObjectURL(capturedPreview)
            setCapturedPhoto(file)
            setCapturedPreview(URL.createObjectURL(file))
            setCameraOpen(false)
          }}
          onCancel={() => setCameraOpen(false)}
          facing="user"
        />
      </Drawer>

      {/* Sprint 0149 — Business card scan (back camera for documents) */}
      <Drawer
        title={
          <Space>
            <ScanOutlined />
            <span>Scan Business Card</span>
          </Space>
        }
        width={520}
        open={cardCameraOpen}
        onClose={() => setCardCameraOpen(false)}
        destroyOnClose
      >
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          message="Hold card flat, fill the frame, good light. Name, company, mobile, email auto-fill after capture."
        />
        <CameraCapture
          onCapture={(file) => extractCardMut.mutate(file)}
          onCancel={() => setCardCameraOpen(false)}
          facing="environment"
        />
      </Drawer>

      {/* Sprint VIS-PHOTO-1 — Retake for an already-logged visitor */}
      <Drawer
        title={retakeTarget
          ? `Photo — ${retakeTarget.visitor_pass_no} ${retakeTarget.visitor_name}`
          : ''}
        width={520}
        open={!!retakeTarget}
        onClose={() => setRetakeTarget(null)}
        destroyOnClose
      >
        {retakeTarget && (
          <CameraCapture
            existingUrl={retakeTarget.photo_url}
            onCapture={(file) =>
              retakeMutation.mutate({ id: retakeTarget.id, file })
            }
            onCancel={() => setRetakeTarget(null)}
            facing="user"
          />
        )}
      </Drawer>
    </div>
  )
}
